// src/api/backendApi.js
//
// ──────────────────────────────────────────────────────────────
//  WebIdeal-Jobs API Client, Connects to backend Express API
// ──────────────────────────────────────────────────────────────

// Backend URL is env-driven so the app works in both local dev and prod.
// Set REACT_APP_API_URL in `.env` (no trailing slash). Falls back to localhost
// for first-time local setup.
const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");
const API_BASE = `${API_ROOT}/api`;

// ──────────────────────────────────────────────
//  HELPER, Get auth token from localStorage
// ──────────────────────────────────────────────
function getToken() {
  const raw = localStorage.getItem("token");
  // Guard: treat "undefined" / "null" strings as missing token
  if (!raw || raw === "undefined" || raw === "null") return null;
  return raw;
}

function setToken(token) {
  // Guard: never store falsy or the literal string 'undefined'
  if (!token || token === "undefined" || token === "null") {
    console.error("[Auth] setToken called with invalid token:", token);
    return;
  }
  localStorage.setItem("token", token);
}

function removeToken() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

// ✅ FIX: Normalize user object so name is ALWAYS stored under a consistent
// set of fields regardless of whether it came from Google OAuth or email login.
// Google returns: { name, email, picture }, we map picture → avatar_url
// Email login returns: { full_name, email }, we map full_name → name
function normalizeUser(raw) {
  if (!raw) return null;
  // Requirement: name must be primary, fallback to full_name
  const name =
    raw.name ||
    raw.full_name ||
    raw.display_name ||
    raw.displayName ||
    raw.google_name ||
    "";

  return {
    ...raw,
    name: name,
    full_name: raw.full_name || name,
    avatar_url: raw.avatar_url || raw.picture || raw.photoURL || "",
  };
}

function setStoredUser(user) {
  // Always store the normalized form so all reads are consistent
  localStorage.setItem("user", JSON.stringify(normalizeUser(user)));
}

// ──────────────────────────────────────────────
//  CORE API REQUEST FUNCTION
// ──────────────────────────────────────────────
async function apiRequest(path, options = {}) {
  const token = getToken();

  // Requirement 3: Debug, log token before every API call
  // Helps diagnose "Not authorized, no token" errors
  if (process.env.NODE_ENV === "development") {
    if (token) {
      console.log(`[API] ${options.method || "GET"} ${path}, token: ...${token.slice(-8)}`);
    } else {
      console.warn(`[API] ${options.method || "GET"} ${path}, ⚠️ NO TOKEN IN STORAGE`);
    }
  }

  const headers = {
    ...(options.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    // Requirement 1 & 8: Always send token if present
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data.message || `API error: ${res.status}`);
    error.status = res.status;

    // 🔥 NEW FIX: handle 401 globally (DO NOT logout automatically)
    if (res.status === 401) {
      console.warn("⚠️ Unauthorized request, token may be expired");
    }

    throw error;
  }

  return res.json();
}

// ──────────────────────────────────────────────
//  GENERIC ENTITY CLIENT
// ──────────────────────────────────────────────
function createEntityClient(entityPath) {
  return {
    list: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      const url = query ? `/${entityPath}?${query}` : `/${entityPath}`;
      try {
        return await apiRequest(url);
      } catch (err) {
        return [];
      }
    },
    get: async (id) => {
      try {
        return await apiRequest(`/${entityPath}/${id}`);
      } catch (err) {
        return null;
      }
    },
    filter: async (criteria = {}) => {
      const query = new URLSearchParams(criteria).toString();
      const url = query ? `/${entityPath}?${query}` : `/${entityPath}`;
      const all = await apiRequest(url);
      return all.filter((item) =>
        Object.entries(criteria).every(([key, value]) => item[key] === value)
      );
    },
    create: (data) =>
      apiRequest(`/${entityPath}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id, data) =>
      apiRequest(`/${entityPath}/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id) =>
      apiRequest(`/${entityPath}/${id}`, {
        method: "DELETE",
      }),
  };
}

// ──────────────────────────────────────────────
//  AUTH MODULE
// ──────────────────────────────────────────────
export const backendApi = {
  auth: {
    getToken: () => getToken(),

    isAuthenticated: async () => {
      const token = getToken();
      if (!token) return false;
      try {
        await apiRequest("/auth/me");
        return true;
      } catch (err) {
        // Only clear on explicit 401, not on network errors or backend restarts
        if (err.status === 401) removeToken();
        return false;
      }
    },

    // Get current user, always returns normalized user so name is consistent
    // me() always returns null on failure, never throws, never clears token.
    // The caller (initAuth) ignores failures and preserves localStorage.
    me: async () => {
      const token = getToken();
      if (!token) return null;
      try {
        const raw = await apiRequest("/auth/me");
        const user = normalizeUser(raw);
        setStoredUser(user);
        return user;
      } catch {
        // Any error (401, 500, network), return null, keep token intact
        return null;
      }
    },

    login: async (email, password) => {
      const raw = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      // Backends vary: some return 'token', some 'accessToken', some 'jwt'
      const jwtToken = raw.token || raw.accessToken || raw.jwt;
      if (!jwtToken) console.error("[Auth] Login response has no token field:", Object.keys(raw));
      setToken(jwtToken);
      const user = normalizeUser(raw);
      setStoredUser(user);
      return user;
    },

    googleLogin: async (token) => {
      const raw = await apiRequest("/auth/google", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      const jwtToken = raw.token || raw.accessToken || raw.jwt;
      if (!jwtToken) console.error("[Auth] Google login response has no token field:", Object.keys(raw));
      setToken(jwtToken);
      const user = normalizeUser(raw);
      setStoredUser(user);
      return user;
    },

    register: async (userData) => {
      const raw = await apiRequest("/auth/register", {
        method: "POST",
        body: JSON.stringify(userData),
      });
      const jwtToken = raw.token || raw.accessToken || raw.jwt;
      if (!jwtToken) console.error("[Auth] Register response has no token field:", Object.keys(raw));
      setToken(jwtToken);
      const user = normalizeUser(raw);
      setStoredUser(user);
      return user;
    },

    sendOTP: async (email) => {
      return apiRequest("/auth/send-otp", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    },

    verifyOTP: async (email, otp) => {
      const raw = await apiRequest("/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      });
      const jwtToken = raw.token || raw.accessToken || raw.jwt;
      if (!jwtToken) console.error("[Auth] VerifyOTP response has no token field:", Object.keys(raw));
      setToken(jwtToken);
      const user = normalizeUser(raw);
      setStoredUser(user);
      return user;
    },

    // Does NOT use window.location, callers use React Router <Navigate> instead
    logout: () => {
      removeToken();
    },

    updateProfile: (data) =>
      apiRequest("/profiles/contact", {
        method: "PUT",
        body: JSON.stringify(data),
      }),

    updatePassword: async (currentPassword, newPassword) => {
      return apiRequest("/auth/change-password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
    },

    forgotPassword: async (email) => {
      return apiRequest("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    },

    resetPassword: async (token, newPassword) => {
      return apiRequest(`/auth/reset-password/${token}`, {
        method: "POST",
        body: JSON.stringify({ password: newPassword }),
      });
    },
  },

  // ──────────────────────────────────────────────
  //  ENTITIES
  // ──────────────────────────────────────────────
  entities: {
    Company: createEntityClient("companies"),
    Organization: createEntityClient("companies"),
    Job: {
      ...createEntityClient("jobs"),
      getRecommended: () => apiRequest("/jobs/recommended"),
      getRecent: () => apiRequest("/jobs/recent"),
    },
    Application: {
      ...createEntityClient("applications"),
      getByJob: (jobId) => apiRequest(`/applications/job/${jobId}`),
      updateStatus: (id, status) =>
        apiRequest(`/applications/${id}/status`, {
          method: "PUT",
          body: JSON.stringify({ status }),
        }),
    },
    UserProfile: {
      ...createEntityClient("profiles"),
      getMe: () => apiRequest("/profiles/me"),

      uploadResume: (formData) =>
        apiRequest("/profiles/upload-resume", { method: "POST", body: formData }),
      getResume: () => apiRequest("/profiles/resume"),
      deleteResume: () => apiRequest("/profiles/resume", { method: "DELETE" }),

      updateHeadline: (headline) =>
        apiRequest("/profiles/headline", {
          method: "PUT",
          body: JSON.stringify({ headline }),
        }),

      getSkills: () => apiRequest("/profiles/skills"),
      addSkill: (skill) =>
        apiRequest("/profiles/skills", {
          method: "POST",
          body: JSON.stringify(skill),
        }),
      deleteSkill: (id) =>
        apiRequest(`/profiles/skills/${id}`, { method: "DELETE" }),

      getEmployment: () => apiRequest("/profiles/employment"),
      addEmployment: (data) =>
        apiRequest("/profiles/employment", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      updateEmployment: (id, data) =>
        apiRequest(`/profiles/employment/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      deleteEmployment: (id) =>
        apiRequest(`/profiles/employment/${id}`, { method: "DELETE" }),

      getEducation: () => apiRequest("/profiles/education"),
      addEducation: (data) =>
        apiRequest("/profiles/education", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      updateEducation: (id, data) =>
        apiRequest(`/profiles/education/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      deleteEducation: (id) =>
        apiRequest(`/profiles/education/${id}`, { method: "DELETE" }),

      getITSkills: () => apiRequest("/profiles/it-skills"),
      addITSkill: (data) =>
        apiRequest("/profiles/it-skills", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      deleteITSkill: (id) =>
        apiRequest(`/profiles/it-skills/${id}`, { method: "DELETE" }),

      getProjects: () => apiRequest("/profiles/projects"),
      addProject: (data) =>
        apiRequest("/profiles/projects", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      updateProject: (id, data) =>
        apiRequest(`/profiles/projects/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      deleteProject: (id) =>
        apiRequest(`/profiles/projects/${id}`, { method: "DELETE" }),

      updateSummary: (data) =>
        apiRequest("/profiles/summary", {
          method: "PUT",
          body: JSON.stringify(data),
        }),

      getAccomplishments: () => apiRequest("/profiles/accomplishments"),
      addAccomplishment: (data) =>
        apiRequest("/profiles/accomplishments", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      deleteAccomplishment: (id) =>
        apiRequest(`/profiles/accomplishments/${id}`, { method: "DELETE" }),

      updateCareer: (data) =>
        apiRequest("/profiles/career", {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      updatePersonal: (data) =>
        apiRequest("/profiles/personal", {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      updateHeaderInfo: (data) =>
        apiRequest("/profiles/header", {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      updateContactInfo: (data) =>
        apiRequest("/profiles/contact", {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      uploadPhoto: (formData) =>
        apiRequest("/profiles/upload-photo", { method: "POST", body: formData }),
    },
    SavedJob: createEntityClient("saved-jobs"),
    Notification: {
      ...createEntityClient("notifications"),
      getAll: () => apiRequest("/notifications"),
      getUnreadCount: () => apiRequest("/notifications/unread-count"),
      markRead: (id) =>
        apiRequest(`/notifications/${id}/read`, { method: "PUT" }),
      markAll: () =>
        apiRequest("/notifications/mark-all-read", { method: "PUT" }),
    },
    Message: createEntityClient("messages"),
    Dashboard: {
      analytics: () => apiRequest("/dashboard/analytics"),
    },
    Candidate: {
      create: (data) =>
        apiRequest("/auth/register", {
          method: "POST",
          body: JSON.stringify({
            full_name: data.full_name,
            email: data.email,
            password: data.password,
            mobile: data.mobile,
            work_status: data.work_status,
            receive_updates: data.receive_updates,
            role: "jobseeker",
          }),
        }).then((res) => {
          setToken(res.token);
          const user = normalizeUser(res);
          setStoredUser(user);
          return user;
        }),
      list: () => apiRequest("/profiles/all"),
      filter: async (criteria = {}) => {
        const profiles = await apiRequest("/profiles/all");
        const list = Array.isArray(profiles) ? profiles : [];
        return list.filter((item) =>
          Object.entries(criteria).every(([key, value]) => item[key] === value)
        );
      },
    },

    // NOTE: HRMS data is served by the dedicated HRMS client
    // (src/HRMS Site/api/backendApi.js → HRMS backend). The old inline HRMS
    // mock rows were removed. `Interview` remains for the Recruit module.
    Interview: {
      list: () => Promise.resolve([]),
      create: (data) => Promise.resolve({ id: Date.now(), ...data }),
      update: (id, data) => Promise.resolve({ id, ...data }),
      delete: () => Promise.resolve({ success: true }),
    },
  },

  Settings: {
    getAll: () => apiRequest("/settings"),
    updateAccount: (data) =>
      apiRequest("/settings/account", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    changePassword: (data) =>
      apiRequest("/settings/change-password", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    logoutFromAll: () =>
      apiRequest("/settings/logout-from-all", { method: "PUT" }),
    updatePrivacy: (data) =>
      apiRequest("/settings/privacy", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    updateNotifications: (data) =>
      apiRequest("/settings/notifications", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    updateResume: (data) =>
      apiRequest("/settings/resume", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    deleteAccount: () =>
      apiRequest("/settings/account", { method: "DELETE" }),
  },

  user: {
    getJobStatus: () => apiRequest("/user/job-status"),
    updateJobStatus: (data) =>
      apiRequest("/user/job-status", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
  },

  jobs: {
    create: (data) =>
      apiRequest("/jobs", { method: "POST", body: JSON.stringify(data) }),
    getAll: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      const url = query ? `/jobs?${query}` : "/jobs";
      return apiRequest(url);
    },
    getById: (id) => apiRequest(`/jobs/${id}`),
  },

  applications: {
    apply: (data) =>
      apiRequest("/applications", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    getMy: () => apiRequest("/applications"),
  },

  integrations: {
    Core: {
      UploadFile: async ({ file }) => {
        const formData = new FormData();
        formData.append("resume", file);
        const token = getToken();
        const res = await fetch(`${API_BASE}/profile/upload-resume`, {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Upload failed");
        }
        const data = await res.json();
        return { url: `${API_BASE.replace("/api", "")}${data.resume_url}` };
      },
      InvokeLLM: async ({ prompt, add_context_from_internet = false }) => {
        return { response: "AI feature coming soon." };
      },
    },
  },
};

export default backendApi;